import { computePriceWithMargin } from "../helpers/computePriceWithMargin";
import { ProcessStep, MaterialLayout, ProductLine } from "../types";
import { OptimizedMachineTimes } from "./computeOptimizedMachineTimes";
import { computeProcessPrice } from "./computeProcessPrice";
import { isProductProcessStep } from "./isProductProcessStep";

export type ComputeProcessesPriceQuoteRequestLine = {
  quantity: number;
  reworkRatio: number | null;
  quantityInAssembly: number | null;
  addedValueMarginRatio: number | null;
  productsMarginRatio: number | null;
  processSteps: (ProcessStep & {
    materialLayout: MaterialLayout | null;
    productLines: ProductLine[];
  })[];
  optimizedMachineTimes: OptimizedMachineTimes;
};

export const computeProcessesPrice = ({
  quantity,
  reworkRatio,
  quantityInAssembly,
  addedValueMarginRatio,
  productsMarginRatio,
  processSteps,
  optimizedMachineTimes,
}: ComputeProcessesPriceQuoteRequestLine): {
  partProcessesPrice: number;
  partProcessesCost: number;
  partProcessesMarginPrice: number;
  costs: {
    partAddedValueProcessesCost: number;
    partProductsProcessesCost: number;
  };
  margins: {
    partAddedValueProcessesMarginPrice: number;
    partProductsProcessesMarginPrice: number;
  };
  prices: {
    partAddedValueProcessesPrice: number;
    partProductsProcessesPrice: number;
  };
} => {
  const {
    productsProcessesPrice,
    productsProcessesCost,
    addedValueProcessesPrice,
    addedValueProcessesCost,
  } = processSteps.reduce(
    (accumulator, step) => {
      const { partProcessPrice, partProcessCost, partProcessMarginPrice } = computeProcessPrice({
        quantity,
        quantityInAssembly,
        reworkRatio,
        processStep: step,
        optimizedMachineTimes,
      });

      if (isProductProcessStep(step)) {
        accumulator.productsProcessesPrice += partProcessPrice;
        accumulator.productsProcessesCost += partProcessCost;
        accumulator.productsProcessesMarginPrice += partProcessMarginPrice;
      } else {
        accumulator.addedValueProcessesPrice += partProcessPrice;
        accumulator.addedValueProcessesCost += partProcessCost;
        accumulator.addedValueProcessesMarginPrice += partProcessMarginPrice;
      }

      return accumulator;
    },
    {
      productsProcessesPrice: 0,
      productsProcessesCost: 0,
      productsProcessesMarginPrice: 0,
      addedValueProcessesPrice: 0,
      addedValueProcessesCost: 0,
      addedValueProcessesMarginPrice: 0,
    },
  );

  const addedValueProcessesPriceWithMarkup = computePriceWithMargin(
    addedValueProcessesPrice,
    addedValueMarginRatio ?? 0,
  );
  const addedValueProcessesMarginPriceWithMarkup =
    addedValueProcessesPriceWithMarkup - addedValueProcessesCost;

  const productsProcessesPriceWithMarkup = computePriceWithMargin(
    productsProcessesPrice,
    productsMarginRatio ?? 0,
  );
  const productsProcessesMarginPriceWithMarkup =
    productsProcessesPriceWithMarkup - productsProcessesCost;

  const partProcessesCost = productsProcessesCost + addedValueProcessesCost;
  const partProcessesMarginPrice =
    productsProcessesMarginPriceWithMarkup + addedValueProcessesMarginPriceWithMarkup;
  const partProcessesPrice = productsProcessesPriceWithMarkup + addedValueProcessesPriceWithMarkup;

  return {
    partProcessesCost,
    partProcessesMarginPrice,
    partProcessesPrice,
    costs: {
      partAddedValueProcessesCost: addedValueProcessesCost,
      partProductsProcessesCost: productsProcessesCost,
    },
    margins: {
      partAddedValueProcessesMarginPrice: addedValueProcessesMarginPriceWithMarkup,
      partProductsProcessesMarginPrice: productsProcessesMarginPriceWithMarkup,
    },
    prices: {
      partAddedValueProcessesPrice: addedValueProcessesPriceWithMarkup,
      partProductsProcessesPrice: productsProcessesPriceWithMarkup,
    },
  };
};
